// @rpg-unit/follow-camera: the third-person follow camera (tier 4, a feature; composition framework, decision 0077),
// cut from spike 002's `src/camera/index.ts` and `src/valley/camera.ts`. @rpg/camera's orbit rig wired into a game:
//   - profiles: the camera profile presets (@rpg-unit/camera-profile-presets) on a three-layer stack (mode, context,
//     overlay), each requested on its own layer by name;
//   - rig settings: the collision stack's tuned numbers (this unit's `camera-rig` table: probe margin, thin and fade
//     widths, arm rates, predictive casts, crane steps, fades);
//   - collision: the game's collision world and layers (@rpg/camera's shape casts), or none;
//   - look input: a per-frame delta from the game's input, read once per rendered frame on real time (live through
//     hit-stop), scaled by the player's settings `camera.sensitivity` and `camera.invertY`;
//   - a target: anything speaking @rpg/camera's `CameraTarget`; `followSteppedTarget` pushes a sim body into a
//     SteppedTarget once per sim step and cuts on a teleport;
//   - mouse look: `pointerLockOnPress` asks for pointer lock on a mouse press.
// Clocks (spike 002's decisions 0010, 0011): look input in the runtime's input phase, the rig in the late phase
// (order -10) after the frame's sim steps, reading the target at the frame's alpha. Nothing here runs in the sim
// phase except the stepped target's push, which only reads the sim.
//
// Seams (rule 4): it meets other features at @rpg/camera's rig (impact feedback shakes and kicks `rig.shake`),
// at profile requests by name (a game's controller, dialogue or combat asks for "combat", "dialogue"), and at the
// player's settings by id. It imports no other feature.
import type * as THREE from "three";
import { CameraRig, ProfileStack, SteppedTarget, type BlendOptions, type CameraTarget, type RigCollision, type RigSettings, type TargetState } from "@rpg/camera";
import type { Runtime, Vec2 } from "@rpg/runtime";
import { CAMERA_PROFILES_TABLE, presetProfiles, PROFILE_LAYERS, type CameraProfilePreset } from "@rpg-unit/camera-profile-presets";
import table from "./content/tables/camera-rig.json";
import schema from "./content/schemas/camera-rig.schema.json";

/** This unit's version (the manifest's and package.json's). */
export const FOLLOW_CAMERA_VERSION = "0.1.0" as const;

/** The rig's tuned settings as a row of the `camera-rig` table (`src/content/schemas/camera-rig.schema.json`): the
 *  collision stack's numbers, the profile the camera starts on, and the reason for them. */
export interface CameraRigPreset extends RigSettings {
  id: string;
  /** The profile the stack starts on (a camera profile's id). */
  initial: string;
  reason: string;
}

/** The rig settings as a content table (`{ table: "camera-rig", version, rows }`; one row, `default`). */
export const CAMERA_RIG_TABLE = table as { table: "camera-rig"; version: number; rows: CameraRigPreset[] };

/** The table's row schema (JSON Schema). */
export const CAMERA_RIG_SCHEMA = schema as Record<string, unknown>;

/** The content this unit adds to a game, in @rpg/content's shape. */
export const FOLLOW_CAMERA_CONTENT = {
  package: "@rpg-unit/follow-camera",
  version: FOLLOW_CAMERA_VERSION,
  tables: [CAMERA_RIG_TABLE],
  schemas: { "camera-rig": CAMERA_RIG_SCHEMA },
} as const;

/** The player's settings this feature reads, by id, with the value it uses when the game has none. */
export const FOLLOW_CAMERA_SETTINGS = {
  /** Look speed, × (a range row; 1 as bound). */
  sensitivity: { id: "camera.sensitivity", fallback: 1 },
  /** Invert the vertical look (a toggle row). */
  invertY: { id: "camera.invertY", fallback: false },
} as const;

/** A rig preset's @rpg/camera settings (its numbers only). */
export function rigSettings(row: CameraRigPreset = CAMERA_RIG_TABLE.rows[0]!): RigSettings {
  const { probeMargin, thinWidth, fadeWidth, armIn, armOut, armInSpeed, minArm, predict, craneSteps, fadeOpacity, fadeRate, targetFadeStart } = row;
  return { probeMargin, thinWidth, fadeWidth, armIn, armOut, armInSpeed, minArm, predict, craneSteps, fadeOpacity, fadeRate, targetFadeStart };
}

/** Where look input comes from: a per-frame delta in radians (x turns right, y raises the camera). */
export type LookSource = (realDt: number) => Vec2;

/** A mounted follow camera. */
export interface FollowCamera {
  rig: CameraRig;
  /** Ask for a profile by name on its preset's layer (or the one given). */
  request(name: string, o?: BlendOptions): void;
  /** Release a layer (blends back to the profile under it). */
  release(layer: string, o?: Omit<BlendOptions, "layer">): void;
  /** Stop driving the camera. */
  unmount(): void;
}

/** What `mountFollowCamera` takes. */
export interface FollowCameraOptions {
  runtime: Runtime;
  camera: THREE.PerspectiveCamera;
  target: CameraTarget;
  /** The profile rows (default: the camera profile presets). A game passes its content's `camera-profiles` rows. */
  profiles?: readonly CameraProfilePreset[];
  /** The rig row (default: this unit's `default`). */
  rig?: CameraRigPreset;
  collision?: RigCollision | null;
  look?: LookSource;
  /** The starting yaw (rad). */
  yaw?: number;
  /** A setting's value now, by id (`FOLLOW_CAMERA_SETTINGS`); undefined for the fallback. Read every frame, so the
   *  settings screen changes look speed and inversion live. */
  setting?: (id: string) => unknown;
}

/** What's wrong between a rig row and the profiles it starts on (empty: its starting profile is among them). */
export function checkRigProfiles(rig: CameraRigPreset, profiles: readonly CameraProfilePreset[]): string[] {
  return profiles.some((p) => p.id === rig.initial) ? [] : [`the starting profile "${rig.initial}" is not in camera-profiles`];
}

/**
 * What's wrong with a rig row against the profiles it serves (empty: none), beyond its schema: the arm pulls in
 * faster than it eases out (else an occluder cuts into view before the camera moves); the shortest arm is shorter
 * than every profile's distance (else the camera cranes even with nothing in the way); a thin collider is narrower
 * than a fadeable one; the predictive casts look further ahead one by one; the starting profile exists.
 */
export function checkRig(rig: CameraRigPreset, profiles: readonly CameraProfilePreset[]): string[] {
  const out = checkRigProfiles(rig, profiles);
  if (!(rig.armIn > rig.armOut)) out.push(`the arm must pull in faster than it eases out (armIn ${rig.armIn}, armOut ${rig.armOut})`);
  for (const p of profiles) if (!(rig.minArm < p.distance)) out.push(`minArm ${rig.minArm} m is not shorter than ${p.id}'s distance ${p.distance} m`);
  if (!(rig.thinWidth < rig.fadeWidth)) out.push(`thinWidth ${rig.thinWidth} must be under fadeWidth ${rig.fadeWidth}`);
  for (let i = 1; i < rig.predict.length; i++) if (!(rig.predict[i]! > rig.predict[i - 1]!)) out.push(`predict must increase (${rig.predict.join(", ")})`);
  return out;
}

/**
 * Drive `camera` after `target`: look input in the input phase (handler "camera: look"), the rig in the late phase
 * (order -10, "camera: rig"; before anything that draws the target, so both read the same alpha).
 */
export function mountFollowCamera(o: FollowCameraOptions): FollowCamera {
  const rows = o.profiles ?? CAMERA_PROFILES_TABLE.rows;
  const rigRow = o.rig ?? CAMERA_RIG_TABLE.rows[0]!;
  const bad = checkRigProfiles(rigRow, rows);
  if (bad.length) throw new Error(`camera: ${bad[0]}`);
  const { profiles, layers } = presetProfiles(rows);
  const stack = new ProfileStack(profiles, { initial: rigRow.initial, layers: PROFILE_LAYERS });
  const rig = new CameraRig({ camera: o.camera, target: o.target, profiles: stack, settings: rigSettings(rigRow), collision: o.collision ?? null, yaw: o.yaw ?? 0 });
  const read = (s: { id: string; fallback: unknown }): unknown => o.setting?.(s.id) ?? s.fallback;
  const invert = (): number => (read(FOLLOW_CAMERA_SETTINGS.invertY) === true ? -1 : 1);
  const sens = (): number => Number(read(FOLLOW_CAMERA_SETTINGS.sensitivity));
  const offs = [
    o.runtime.on(
      "input",
      (c) => {
        if (!o.look) return;
        const d = o.look(c.realDt);
        if (d.x !== 0 || d.y !== 0) rig.look(d.x * sens(), d.y * sens() * invert());
      },
      { name: "camera: look" },
    ),
    o.runtime.on("late", (c) => void rig.update(c), { order: -10, name: "camera: rig" }),
  ];
  return {
    rig,
    request: (name, b = {}) => rig.request(name, { layer: b.layer ?? layers[name], ...(b.blend !== undefined ? { blend: b.blend } : {}) }),
    release: (layer, b) => rig.release(layer, b),
    unmount: () => offs.forEach((f) => f()),
  };
}

/**
 * A SteppedTarget fed from the sim: once per sim step (order `order`, default 1e8, after the sim's own systems) it
 * reads `read()` and pushes it; a jump further than `teleportOver` metres in one step (a warp, a dismount) is a
 * teleport, so the camera cuts instead of swinging across. Returns the target and the remover.
 */
export function followSteppedTarget(o: { runtime: Runtime; read: () => TargetState; teleportOver?: number; order?: number; name?: string }): { target: SteppedTarget; stop: () => void } {
  const target = new SteppedTarget(o.read());
  const over = o.teleportOver ?? 2;
  let last = o.read().position;
  const stop = o.runtime.on(
    "sim",
    () => {
      const s = o.read();
      const jump = Math.hypot(s.position.x - last.x, s.position.y - last.y, s.position.z - last.z);
      if (jump > over) target.teleport(s);
      else target.push(s);
      last = s.position;
    },
    { order: o.order ?? 1e8, name: o.name ?? "camera: target" },
  );
  return { target, stop };
}

/**
 * Mouse look wants the pointer locked: a mouse press on the element asks for it (an input's DOM attach then sends
 * locked motion to the look action's mouse binding). Touch and the pad need nothing here. Returns the remover.
 */
export function pointerLockOnPress(el: HTMLElement): () => void {
  const down = (e: PointerEvent): void => {
    if (e.pointerType !== "mouse" || document.pointerLockElement === el) return;
    try {
      void (el.requestPointerLock?.() as Promise<void> | undefined)?.catch?.(() => {});
    } catch {
      // not allowed here (a sandbox, a test page): mouse look falls back to a drag
    }
  };
  el.addEventListener("pointerdown", down);
  return () => el.removeEventListener("pointerdown", down);
}
